import {
  memo,
  useEffect,
  useState,
  MouseEvent,
  useRef,
  useCallback,
} from "react";
import { FolderPlus, FolderX, Loader } from "lucide-react";
import { Button } from "primereact/button";
import Input from "@/components/common/Input";
import SearchInput from "@/components/common/SearchInput";
import CategoryNode from "./CategoryNode";
import Uploader from "../../Uploader";
import { FontCategory } from "@/interfaces/font.interface";
import fontCategoryApi from "@/api/font-category";
import { uploadManyFonts } from "@/utils/font";
import RootCategoryNode from "./RootCategoryNode";

const rootCategory: FontCategory = {
  _id: "",
  ancestorIds: [],
  name: "__root__",
  createdAt: "",
  updateDAt: "",
};

interface Props {
  onChange?: (category?: FontCategory) => void;
}

function CategoryTree({ onChange }: Props) {
  const [categoryList, setCategoryList] = useState<FontCategory[]>([]);
  const [creating, setCreating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<FontCategory>();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const treeRef = useRef<HTMLDivElement>(null);

  const getCategoryList = useCallback(() => {
    fontCategoryApi
      .getMany({ sort: "-createdAt", search })
      .then((data) => {
        const categoryList = data.data;
        setCategoryList(categoryList);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => {
    getCategoryList();
  }, [getCategoryList]);

  useEffect(() => {
    if (!selectedCategory || selectedCategory._id === rootCategory._id) return;
    const existing = categoryList.some((c) =>
      [selectedCategory._id, ...selectedCategory.ancestorIds].includes(c._id),
    );
    if (!existing) {
      setSelectedCategory(undefined);
      onChange?.(undefined);
    }
  }, [selectedCategory, categoryList, onChange]);

  const onCreate = (name: string) => {
    setCreating(false);
    if (!name) return;
    fontCategoryApi
      .createOne({ name })
      .then((data) => {
        setCategoryList((pre) => [data.data, ...(pre || [])]);
      })
      .catch(() => {});
  };

  const onSelect = (category?: FontCategory) => {
    setSelectedCategory(category);
    onChange?.(category);
  };

  const onDelete = (category: FontCategory) => {
    fontCategoryApi
      .deleteOneById(category._id)
      .then((data) => {
        const deletedCategory = data.data;
        setCategoryList((state) =>
          state.filter((c) => c._id !== deletedCategory._id),
        );
        if (
          selectedCategory?._id === deletedCategory._id ||
          selectedCategory?.ancestorIds.includes(deletedCategory._id)
        )
          onSelect(undefined);
      })
      .catch(() => {});
  };

  const onUploadFolder = async (
    files: File[],
    onProgress: (data: string) => void,
  ) => {
    await uploadManyFonts(files, selectedCategory?._id, onProgress).finally(
      () => onFolderUploaded(),
    );
  };

  const onFolderUploaded = () => {
    if (selectedCategory) {
      selectedCategory.actions?.read?.();
    } else {
      getCategoryList();
    }
  };

  const onClickOutSide = (
    event: MouseEvent<HTMLDivElement, globalThis.MouseEvent>,
  ) => {
    const treeElement = treeRef.current;
    if (!treeElement) return;
    const children = treeElement.children;
    const target = event.target;
    if (target === children[0] || target === children[1]) {
      setSelectedCategory(undefined);
      onChange?.(undefined);
    }
  };

  return (
    <div
      ref={treeRef}
      className="flex h-full flex-col"
      onClick={onClickOutSide}
    >
      <div className="flex flex-col gap-3.25 border-b-1 border-gray-300 p-4">
        <div className="ml-auto flex w-fit items-center gap-2">
          <div className="mr-auto flex items-center gap-2">
            {loading && <Loader className="animate-spin" size={20} />}
          </div>
          {selectedCategory && (
            <Button
              style={{ width: 28, height: 28 }}
              severity="warning"
              tooltip="Unselect category"
              tooltipOptions={{ position: "top" }}
              icon={<FolderX size={18} />}
              onClick={() => onSelect(undefined)}
            />
          )}
          <Uploader accept="font" type="folder" handler={onUploadFolder} />
          <Button
            style={{ width: 28, height: 28 }}
            tooltip="New category"
            tooltipOptions={{ position: "top" }}
            icon={<FolderPlus size={18} />}
            onClick={() =>
              selectedCategory
                ? selectedCategory.actions?.create?.()
                : setCreating(true)
            }
          />
        </div>
        <SearchInput onChange={() => setLoading(true)} onSearch={setSearch} />
      </div>
      <div className="flex-1 overflow-auto">
        <RootCategoryNode
          data={rootCategory}
          selectedCategory={selectedCategory}
          onSelect={onSelect}
        />
        {creating && (
          <div className="flex items-center gap-1 pl-2">
            <i className="pi pi-chevron-right" style={{ fontSize: 12 }}></i>
            <Input
              type="text"
              style={{ border: "none" }}
              autoFocus
              onBlur={(event) => onCreate(event.target.value)}
              onKeyDown={(event) => {
                if (event.code === "Enter") {
                  event.currentTarget.blur();
                }
              }}
            />
          </div>
        )}
        {categoryList.map((c) => (
          <CategoryNode
            key={c._id}
            data={c}
            modifiable
            selectedCategory={selectedCategory}
            onSelect={onSelect}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
}

export default memo(CategoryTree);
